import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { ROUTES, getDefaultRoute, canAccessPath } from "../../../config/routes";

const RoleProtectedRoute = React.memo(({ children, requiredRole }) => {
  const { isAuthenticated, authLoading, user } = useAuth();
  const location = useLocation();

  if (authLoading) {
    return children; // Show the current page content while loading
  }

  if (!isAuthenticated()) {
    return <Navigate to={ROUTES.DEFAULT_UNAUTHENTICATED_ROUTE} replace />;
  }

  // Check if user is verified
  if (user && user.verified === false) {
    return (
      <Navigate to="/verify-email" state={{ email: user.email }} replace />
    );
  }

  // Check if user has a company assignment. The token carries the company as a
  // code string (there's no company id anymore). Admins aren't company-scoped,
  // so the gate doesn't apply to them.
  if (user && !user.isAdmin && !user.company) {
    return <Navigate to="/company-assignment" replace />;
  }

  // Module-based access: the Welcome Survey page requires the welcome_survey
  // module; every other protected page requires the general_suite module. This
  // applies to all user types.
  if (user && !canAccessPath(user.modules, location.pathname)) {
    const defaultRoute = getDefaultRoute(true, user);
    // Guard against redirecting to a path the user also can't access.
    if (defaultRoute !== location.pathname) {
      return <Navigate to={defaultRoute} replace />;
    }
  }

  // Check if user has the required role
  if (requiredRole && user && !requiredRole.includes(user.role)) {
    return <Navigate to={getDefaultRoute(true, user)} replace />;
  }

  return children;
});

export default RoleProtectedRoute;
